/**
 * DIV-044 (issue #386): a new-flow `/cart/add` that Shopify limits by stock.
 *
 * With tracked stock and "Continue selling when out of stock" off, Shopify
 * adds only what is available and answers 422 ("Only X items were added to
 * your cart due to availability"). The redirect submitter used to treat that
 * like success and send the shopper to /cart, where they checked out the
 * partial quantity at the partial price (ScatterMats order S9390: 12 m
 * entered, 4 m in stock, charged for 4 m).
 *
 * Instead: put the matching cart line back to the quantity it had before the
 * click, stay on the product page and tell the shopper. Adding to a cart
 * reserves no stock, so undoing it has no inventory effect.
 *
 * The same price token and `_cpc_properties` merge into one Shopify line, so
 * the line's quantity is read before the add (only when more than one unit is
 * requested — a single unit can't be partially added) and restored, rather
 * than the line being removed outright.
 *
 * DIV-047 (issue #430): every new-flow 422 used to go through this stock
 * path, whatever Shopify actually rejected. A Draft/unpublished/deleted
 * product answers 422 "Cannot find variant" — nothing to do with stock — and
 * was shown the same "not enough stock" wording, which sent merchants and
 * support looking at inventory that was never the cause (X-Pressions CCS,
 * Crisp session_391eadce). `handleStockLimitedAdd` now reads Shopify's 422
 * `description` and only takes the stock path for the stock-shaped messages
 * DIV-044 was written for; "Cannot find variant" gets its own "not available"
 * wording, and anything else gets a generic "couldn't add to cart" message.
 * The cart-line restore (safe no-op when nothing was actually added) still
 * runs in every case, since any of these 422s could in principle follow a
 * partial add.
 */
import $ from "jquery";
import { appState } from "../state/app-state";
import { t } from "../i18n/translate";
import { withDialog } from "../compat/jquery-ui-dialog";

/** The cart line a POST /cart/add lands on: variant + the two CPC-written properties. */
export interface CartLineIdentity {
  variantId: string;
  token: string;
  cpcProperties: string | null;
}

export interface StockGuard {
  line: CartLineIdentity;
  requested: number;
  /** Line quantity before the add; null when it couldn't be read. */
  before: number | null;
}

// "Quantity", not "amount": the shopper changed how many/how much, and
// "amount" reads as the price.
export const NOT_ENOUGH_STOCK_MESSAGE =
  "Sorry, there isn't enough stock for this. Please choose a smaller quantity and try again.";
export const CHECK_CART_MESSAGE =
  "Sorry, there isn't enough stock for the full quantity. Part of it may already be in your cart, so please check your cart before checking out.";
// DIV-047: a Draft, unpublished, deleted, or not-on-Online-Store-channel
// product — Shopify's /cart/add 422 "Cannot find variant". Nothing to do
// with stock.
export const PRODUCT_UNAVAILABLE_MESSAGE =
  "This product isn't available for purchase right now.";
// DIV-047: any other 422 reason. Never shown as a stock message.
export const GENERIC_ADD_TO_CART_ERROR_MESSAGE =
  "Sorry, this couldn't be added to your cart.";

// Shopify's own wording for a stock/availability-limited add (DIV-044's
// cases). Matched against the 422 body's `description` so only these take the
// stock path; everything else is reported as what it actually is.
const STOCK_LIMIT_DESCRIPTION_PATTERNS = [/due to availability/i, /you can only add/i];
// Shopify's literal description when the variant can't be added at all
// because the product is Draft, unpublished, deleted, or off the Online
// Store sales channel.
const UNAVAILABLE_DESCRIPTION_PATTERN = /cannot find variant/i;

function safeJsonParse(text: unknown): any {
  if (typeof text !== "string") return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Shopify's 422 `description`, read from the parsed body or, failing that, the raw text. */
export function cartAddErrorDescription(err: any): string | null {
  const body =
    err && typeof err.responseJSON === "object" && err.responseJSON !== null
      ? err.responseJSON
      : safeJsonParse(err && err.responseText);
  return body && typeof body.description === "string" ? body.description : null;
}

function isStockLimitedDescription(description: string): boolean {
  return STOCK_LIMIT_DESCRIPTION_PATTERNS.some((pattern) => pattern.test(description));
}

function lastString(formData: FormData, key: string): string | null {
  const values = formData.getAll(key);
  const last = values[values.length - 1];
  return typeof last === "string" ? last : null;
}

export function cartLineIdentity(formData: FormData): CartLineIdentity | null {
  const variantId = lastString(formData, "id");
  const token = lastString(formData, "properties[_cpc_custom_cart_item]");
  if (!variantId || !token) return null;
  return { variantId, token, cpcProperties: lastString(formData, "properties[_cpc_properties]") };
}

/** Quantity Shopify will read: the last `quantity` entry (the theme's box may precede CPC's). */
export function requestedQuantity(formData: FormData): number {
  const n = parseInt(lastString(formData, "quantity") ?? "1", 10);
  return isNaN(n) || n < 1 ? 1 : n;
}

export function findCartLine(cart: any, line: CartLineIdentity): any | null {
  const items: any[] = cart && Array.isArray(cart.items) ? cart.items : [];
  for (const item of items) {
    const props = item && item.properties ? item.properties : {};
    if (String(item.variant_id) !== line.variantId) continue;
    if (props._cpc_custom_cart_item !== line.token) continue;
    if (line.cpcProperties !== null && props._cpc_properties !== line.cpcProperties) continue;
    return item;
  }
  return null;
}

async function readCart(): Promise<any | null> {
  try {
    const response = await fetch("/cart.js?cache=false&q=" + Math.random());
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

async function setLineQuantity(key: string, quantity: number): Promise<boolean> {
  try {
    const response = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: key, quantity }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * New flow, more than one unit: read the line's current quantity before adding.
 * null (synchronously) when no guard is needed, so every other add-to-cart
 * keeps its exact request sequence and timing.
 */
export function prepareStockGuard(formData: FormData): Promise<StockGuard> | null {
  if (!appState.cpc_isNewCheckoutFlowEnabled) return null;
  const line = cartLineIdentity(formData);
  const requested = requestedQuantity(formData);
  if (!line || requested <= 1) return null;
  return readCart().then((cart) => {
    const existing = cart ? findCartLine(cart, line) : null;
    return { line, requested, before: cart ? (existing ? existing.quantity : 0) : null };
  });
}

/** Undo whatever Shopify partially added; returns the message to show. */
async function restoreCart(guard: StockGuard | null): Promise<string> {
  if (!guard) return NOT_ENOUGH_STOCK_MESSAGE; // one unit: nothing can have been added
  const cart = await readCart();
  if (!cart) return CHECK_CART_MESSAGE;
  const item = findCartLine(cart, guard.line);
  const after = item ? item.quantity : 0;
  if (guard.before === null) return item ? CHECK_CART_MESSAGE : NOT_ENOUGH_STOCK_MESSAGE;
  if (after <= guard.before) return NOT_ENOUGH_STOCK_MESSAGE;
  console.log("CPC: stock-limited add — restoring cart line from " + after + " to " + guard.before);
  if (await setLineQuantity(item.key, guard.before)) return NOT_ENOUGH_STOCK_MESSAGE;
  if (await setLineQuantity(item.key, guard.before)) return NOT_ENOUGH_STOCK_MESSAGE;
  return CHECK_CART_MESSAGE;
}

function showAddToCartErrorDialog(message: string): void {
  $(".loading-anim-cpc").hide();
  $("#errDialog").attr("title", "Error");
  $("#dialogMsg").text(t(message));
  withDialog(() => ($("#errDialog") as any).dialog({ modal: true }));
  appState.cpc_isCheckoutInProgress = false;
}

/**
 * New-flow 422 from /cart/add: restore the cart (safe even when nothing was
 * added), stay on the page, and show the reason Shopify actually gave —
 * never default to the stock wording for a non-stock rejection (DIV-047).
 */
export async function handleStockLimitedAdd(guard: StockGuard | null, err: any): Promise<void> {
  const description = cartAddErrorDescription(err);
  if (description !== null) console.warn("CPC: Shopify rejected add-to-cart — " + description);

  if (description !== null && UNAVAILABLE_DESCRIPTION_PATTERN.test(description)) {
    await restoreCart(guard);
    console.log("CPC: add to cart rejected — product not available (DIV-047)");
    showAddToCartErrorDialog(PRODUCT_UNAVAILABLE_MESSAGE);
    return;
  }

  if (description === null || !isStockLimitedDescription(description)) {
    await restoreCart(guard);
    console.log("CPC: add to cart rejected (DIV-047)");
    showAddToCartErrorDialog(GENERIC_ADD_TO_CART_ERROR_MESSAGE);
    return;
  }

  const message = await restoreCart(guard);
  console.log("CPC: add to cart limited by stock (DIV-044)");
  showAddToCartErrorDialog(message);
}
